import { useRemoveOrgLogo, useUploadOrgLogo } from '@myrentalmate/api-client';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import type { ChangeEvent } from 'react';

import { apiErrorMessage } from '../../lib/apiError';
import { orgLogoUrl, useMe } from '../../lib/me';

export function LogoSection() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const refresh = { mutation: { onSuccess: () => queryClient.invalidateQueries() } };
  const upload = useUploadOrgLogo(refresh);
  const remove = useRemoveOrgLogo(refresh);
  const url = me ? orgLogoUrl(me) : null;
  const error = upload.error ?? remove.error;

  function chooseFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // so choosing the same file again still fires
    if (file) upload.mutate({ data: { file } });
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, maxWidth: 640, boxSizing: 'border-box' }}>
      <Typography variant="h6">Logo</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Shown in the top bar beside your business name. PNG or JPG, up to 2 MB.
      </Typography>
      <Stack direction="row" useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        {url && (
          <Box
            component="img"
            src={url}
            alt="Your logo"
            sx={{ height: 56, maxWidth: 200, objectFit: 'contain' }}
          />
        )}
        <Button component="label" variant="outlined" disabled={upload.isPending}>
          {upload.isPending ? 'Uploading…' : url ? 'Replace logo' : 'Upload logo'}
          <input
            hidden
            type="file"
            aria-label="Choose a logo"
            accept=".png,.jpg,.jpeg"
            onChange={chooseFile}
          />
        </Button>
        {url && (
          <Button color="error" disabled={remove.isPending} onClick={() => remove.mutate()}>
            Remove
          </Button>
        )}
      </Stack>
      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {apiErrorMessage(error)}
        </Alert>
      ) : null}
    </Paper>
  );
}
